/**
 * Dashboard/Homepage route (issue #423/#450/#451)
 *
 * Renders the user's dashboard widgets on a 12-column responsive grid
 * (`react-grid-layout`, approved #448). In view mode the grid is
 * non-interactive; "Configure Dashboard" enters edit mode, adding drag
 * handles, corner resize handles, an "Add widget" catalog picker (#449),
 * and per-widget remove controls, plus a Save button that persists the
 * layout via the existing REST `PUT /api/v1/user/preferences`.
 *
 * The saved layout is read from the `preferencesSlice` (Redux), hydrated by
 * `usePreferencesSync` on mount (issue #451) rather than queried directly by
 * this route — this keeps a single source of truth store-wide and makes Save
 * an optimistic Redux thunk (`saveDashboardLayout`) with rollback on failure.
 */

import { useMemo, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Responsive as ResponsiveGridLayout, useContainerWidth } from 'react-grid-layout';
import type { Layout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  MyServicesWidget,
  ServiceStatisticsWidget,
  TechnologyRadarWidget,
  CapabilityStatisticsWidget,
  TechnologyStatisticsWidget,
  InformationStatisticsWidget,
  DependencyHealthWidget,
  ServicesByLifecycleWidget,
  ScanLinkageWidget,
  PortfolioHeatmapWidget,
  CrossDivisionDependenciesWidget,
  CapabilityMapWidget,
  CapabilityCoverageWidget,
  TechnologyFootprintWidget,
  TechRationalizationWidget,
  PlatformUsageWidget,
  TechAdoptionTimelineWidget,
  DataDomainUsageWidget,
  DataQualityWidget,
  RecentChangesWidget,
  ServiceComplexityWidget,
  TeamHealthWidget,
  ActiveInitiativesWidget,
  RoadmapTimelineWidget,
  WidgetCatalogDialog,
} from '../components/dashboard';
import { useAvailableWidgets } from '../graphql/queries';
import { useAppSelector } from '../store';
import { selectDashboardLayout } from '../store/features/preferences/preferencesSlice';
import { useDashboardLayoutSave } from '../hooks/useDashboardLayoutSave';
import {
  buildDefaultLayout,
  addWidgetToLayout,
  removeWidgetFromLayout,
  toGridLayout,
  fromGridLayout,
  getAddableWidgets,
  updateWidgetConfig,
  updateWidgetTitle,
  updateWidgetFilters,
} from '../lib/dashboardLayout';
import type { AvailableWidget, DashboardLayout, DashboardWidgetFilter } from '../types/preferences';
import '@tylertech/forge/button';
import '@tylertech/forge/icon';
import '@tylertech/forge/icon-button';
import '@tylertech/forge/banner';
import './index.css';

export const Route = createFileRoute('/')({
  component: DashboardPage,
});

/**
 * Props a widget component may optionally accept to read/update its own
 * persisted config, custom display title, and tag/category filters
 * (issue #489).
 */
export interface WidgetRenderProps {
  config?: Record<string, unknown>;
  onConfigChange?: (config: Record<string, unknown>) => void;
  /** Custom display name overriding the catalog title. */
  title?: string;
  onTitleChange?: (title: string | undefined) => void;
  /** Tag/category filter scoping this widget's data. */
  filters?: DashboardWidgetFilter;
  onFiltersChange?: (filters: DashboardWidgetFilter | undefined) => void;
}

/** Maps a catalog `widgetId` to its rendering component. */
const WIDGET_COMPONENTS: Record<string, (props: WidgetRenderProps) => JSX.Element> = {
  'my-services': MyServicesWidget,
  'service-statistics': ServiceStatisticsWidget,
  'technology-radar': TechnologyRadarWidget,
  'capability-statistics': CapabilityStatisticsWidget,
  'technology-statistics': TechnologyStatisticsWidget,
  'information-statistics': InformationStatisticsWidget,
  'dependency-health': DependencyHealthWidget,
  'services-by-lifecycle': ServicesByLifecycleWidget,
  'scan-linkage': ScanLinkageWidget,
  'portfolio-heatmap': PortfolioHeatmapWidget,
  'cross-division-dependencies': CrossDivisionDependenciesWidget,
  'capability-map': CapabilityMapWidget,
  'capability-coverage': CapabilityCoverageWidget,
  'technology-footprint': TechnologyFootprintWidget,
  'tech-rationalization': TechRationalizationWidget,
  'platform-usage': PlatformUsageWidget,
  'tech-adoption-timeline': TechAdoptionTimelineWidget,
  'data-domain-usage': DataDomainUsageWidget,
  'data-quality': DataQualityWidget,
  'recent-changes': RecentChangesWidget,
  'service-complexity': ServiceComplexityWidget,
  'team-health': TeamHealthWidget,
  'active-initiatives': ActiveInitiativesWidget,
  'roadmap-timeline': RoadmapTimelineWidget,
};

const BREAKPOINTS = { lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 };
const COLS = { lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 };
const DRAG_HANDLE_SELECTOR = '.dashboard-grid__drag-handle';

export function DashboardPage(): JSX.Element {
  const saveDashboardLayout = useDashboardLayoutSave();
  const { data: widgetsData } = useAvailableWidgets();

  const catalog: AvailableWidget[] = widgetsData?.availableWidgets ?? [];
  const savedLayout = useAppSelector(selectDashboardLayout);

  const defaultLayout = useMemo(() => buildDefaultLayout(catalog), [catalog]);
  const effectiveSavedLayout = savedLayout ?? defaultLayout;

  const [editMode, setEditMode] = useState(false);
  const [draftLayout, setDraftLayout] = useState<DashboardLayout>(effectiveSavedLayout);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const { width, containerRef, mounted } = useContainerWidth();

  // In edit mode the grid always shows the draft. Outside edit mode it shows
  // the store's layout directly — this reflects the optimistic update
  // immediately on Save, and the rollback if the save subsequently fails.
  const layout = editMode ? draftLayout : effectiveSavedLayout;
  const addableWidgets = getAddableWidgets(catalog, draftLayout);

  const handleConfigureClick = (): void => {
    if (editMode) {
      // Exiting edit mode without saving discards unsaved changes (UJ-DASH-14).
      setDraftLayout(effectiveSavedLayout);
      setSaveError(null);
      setEditMode(false);
    } else {
      setDraftLayout(effectiveSavedLayout);
      setEditMode(true);
    }
  };

  const handleLayoutChange = (gridLayout: Layout): void => {
    if (!editMode) return;
    setDraftLayout((prev) => fromGridLayout(gridLayout, prev));
  };

  const handleAddWidget = (widget: AvailableWidget): void => {
    setDraftLayout((prev) => addWidgetToLayout(prev, widget));
    setCatalogOpen(false);
  };

  const handleRemoveWidget = (instanceId: string): void => {
    setDraftLayout((prev) => removeWidgetFromLayout(prev, instanceId));
  };

  // Per-widget config changes (issue #491's Technology widget scope toggle, and any
  // future per-widget setting): in edit mode, update the draft only — it persists
  // alongside position changes on the next Save, matching UJ-DASH-14 (discard on exit
  // without save). Outside edit mode there is no draft/Save step, so the change is
  // persisted immediately against the current saved layout. Keyed by `instanceId`
  // (issue #1260), not `widgetId` — multiple instances of the same widget must update
  // independently.
  const handleWidgetConfigChange = (instanceId: string, config: Record<string, unknown>): void => {
    if (editMode) {
      setDraftLayout((prev) => updateWidgetConfig(prev, instanceId, config));
      return;
    }
    void saveDashboardLayout(updateWidgetConfig(effectiveSavedLayout, instanceId, config)).catch(
      () => {
        // Best-effort: failure surfaces via preferencesSlice's rollback + error state,
        // same as a layout Save failure. Nothing further to do here.
      }
    );
  };

  // Custom widget name (issue #489) — same persistence semantics as config changes above.
  const handleWidgetTitleChange = (instanceId: string, title: string | undefined): void => {
    if (editMode) {
      setDraftLayout((prev) => updateWidgetTitle(prev, instanceId, title));
      return;
    }
    void saveDashboardLayout(updateWidgetTitle(effectiveSavedLayout, instanceId, title)).catch(
      () => {
        // Best-effort: same rollback path as handleWidgetConfigChange.
      }
    );
  };

  // Tag/category widget filters (issue #489) — same persistence semantics as config changes above.
  const handleWidgetFiltersChange = (
    instanceId: string,
    filters: DashboardWidgetFilter | undefined
  ): void => {
    if (editMode) {
      setDraftLayout((prev) => updateWidgetFilters(prev, instanceId, filters));
      return;
    }
    void saveDashboardLayout(updateWidgetFilters(effectiveSavedLayout, instanceId, filters)).catch(
      () => {
        // Best-effort: same rollback path as handleWidgetConfigChange.
      }
    );
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    setSaveError(null);
    try {
      // Optimistic save: the store's dashboardLayout updates immediately
      // (before the REST call resolves); on failure it rolls back and this
      // catch surfaces the inline error while remaining in edit mode.
      await saveDashboardLayout(draftLayout);
      setEditMode(false);
    } catch {
      setSaveError('Could not save your dashboard layout. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const ForgeButton = 'forge-button' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const IconButtonElement = 'forge-icon-button' as React.ElementType;
  const BannerElement = 'forge-banner' as React.ElementType;

  return (
    <div className="dashboard-page">
      {/* Dashboard Header */}
      <div className="dashboard-page__header">
        <div className="dashboard-page__header-text">
          <h1 className="dashboard-page__title forge-typography--headline4">Dashboard</h1>
          <p className="dashboard-page__subtitle forge-typography--body1">
            Welcome to the Enterprise Architecture Platform
          </p>
        </div>
        <div className="dashboard-page__header-actions">
          {editMode && (
            <ForgeButton
              type="button"
              variant="outlined"
              onClick={() => setCatalogOpen(true)}
              data-testid="add-widget-button"
            >
              <IconElement name="add" slot="start" aria-hidden="true"></IconElement>
              Add Widget
            </ForgeButton>
          )}
          {editMode && (
            <ForgeButton
              type="button"
              variant="raised"
              onClick={() => void handleSave()}
              disabled={saving}
              data-testid="save-dashboard-button"
            >
              {saving ? 'Saving…' : 'Save'}
            </ForgeButton>
          )}
          <ForgeButton
            type="button"
            variant={editMode ? 'text' : 'outlined'}
            onClick={handleConfigureClick}
            data-testid="configure-dashboard-button"
          >
            <IconElement name="tune" slot="start" aria-hidden="true"></IconElement>
            {editMode ? 'Done' : 'Configure Dashboard'}
          </ForgeButton>
        </div>
      </div>

      {saveError && (
        <BannerElement
          theme="error"
          dismissed={false}
          role="alert"
          className="dashboard-page__error"
        >
          <span>{saveError}</span>
        </BannerElement>
      )}

      {/* Dashboard Widget Grid */}
      <div className="dashboard-page__grid-container" ref={containerRef}>
        {mounted && (
          <ResponsiveGridLayout
            className={`dashboard-grid${editMode ? ' dashboard-grid--edit-mode' : ''}`}
            layouts={{
              lg: toGridLayout(layout),
              md: toGridLayout(layout),
              sm: toGridLayout(layout),
            }}
            breakpoints={BREAKPOINTS}
            cols={COLS}
            width={width}
            rowHeight={80}
            dragConfig={{
              enabled: editMode,
              handle: DRAG_HANDLE_SELECTOR,
              bounded: false,
              threshold: 3,
            }}
            resizeConfig={{ enabled: editMode, handles: ['se'] }}
            onLayoutChange={handleLayoutChange}
          >
            {layout.widgets.map((widget) => {
              const WidgetComponent = WIDGET_COMPONENTS[widget.widgetId];
              if (!WidgetComponent) return null;
              return (
                <div key={widget.instanceId} className="dashboard-grid__item">
                  {editMode && (
                    <div className="dashboard-grid__item-controls">
                      <IconButtonElement
                        className="dashboard-grid__drag-handle"
                        density="dense"
                        aria-label={`Drag to reposition ${widget.widgetId}`}
                      >
                        <IconElement name="drag_indicator" aria-hidden="true"></IconElement>
                      </IconButtonElement>
                      <IconButtonElement
                        density="dense"
                        aria-label="Remove widget"
                        onClick={() => handleRemoveWidget(widget.instanceId)}
                      >
                        <IconElement name="delete" aria-hidden="true"></IconElement>
                      </IconButtonElement>
                    </div>
                  )}
                  <WidgetComponent
                    config={widget.config}
                    onConfigChange={(config) => handleWidgetConfigChange(widget.instanceId, config)}
                    title={widget.title}
                    onTitleChange={(title) => handleWidgetTitleChange(widget.instanceId, title)}
                    filters={widget.filters}
                    onFiltersChange={(filters) =>
                      handleWidgetFiltersChange(widget.instanceId, filters)
                    }
                  />
                </div>
              );
            })}
          </ResponsiveGridLayout>
        )}
      </div>

      <WidgetCatalogDialog
        open={catalogOpen}
        addableWidgets={addableWidgets}
        onAdd={handleAddWidget}
        onClose={() => setCatalogOpen(false)}
      />
    </div>
  );
}
